import { useEffect, useMemo, useState } from "react";
import {
  MapPinOff, CalendarDays, Search, Plus, Trash2, Users, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

function toDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminWfaBulk() {
  const today = new Date();
  const [tanggal, setTanggal] = useState(toDateInput(today));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [catatan, setCatatan] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [w, u] = await Promise.all([
        api.get("/wfa-bulk", { params: { tanggal } }),
        api.get("/pegawai"),
      ]);
      setRows(w.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [tanggal]);

  const filteredUsers = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(s) ||
        u.npp?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s)
    );
  }, [users, q]);

  const existingUserIds = useMemo(() => new Set(rows.map((r) => r.user_id)), [rows]);

  function toggleSelect(uid) {
    setSelected((s) => {
      const next = { ...s };
      if (next[uid]) delete next[uid];
      else next[uid] = true;
      return next;
    });
  }

  function selectAllFiltered() {
    const next = { ...selected };
    filteredUsers.forEach((u) => {
      if (!existingUserIds.has(u.id)) next[u.id] = true;
    });
    setSelected(next);
  }

  function clearSelection() { setSelected({}); }

  async function saveBulk() {
    const uids = Object.keys(selected);
    if (uids.length === 0) {
      toast.error("Pilih minimal 1 pegawai");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/wfa-bulk", {
        user_ids: uids,
        tanggal,
        catatan: catatan.trim() || null,
      });
      toast.success(`${data.inserted} pegawai di-set WFA untuk ${formatTanggalID(tanggal)}`);
      setOpenAdd(false);
      setSelected({});
      setCatatan("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function removeOne(r) {
    if (!window.confirm(`Batalkan WFA untuk ${r.nama} pada ${formatTanggalID(r.tanggal)}?`)) return;
    try {
      await api.delete("/wfa-bulk", {
        data: { user_ids: [r.user_id], tanggal: r.tanggal },
      });
      toast.success("WFA dihapus");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-wfa-bulk">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            WFA Bulk
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Set banyak pegawai WFA (Work From Anywhere) untuk satu tanggal — validasi radius lokasi akan dilewati pada hari tersebut.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="wfa-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button
            onClick={() => { setOpenAdd(true); setQ(""); setSelected({}); setCatatan(""); }}
            className="gap-2 bg-teal-700 hover:bg-teal-800"
            data-testid="wfa-add-btn"
          >
            <Plus size={16} /> Tambah WFA
          </Button>
        </div>
      </div>

      {/* Tanggal picker */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Tanggal
          </Label>
          <Input
            type="date"
            value={tanggal}
            onChange={(e) => setTanggal(e.target.value)}
            className="mt-1.5 max-w-xs"
            data-testid="wfa-tanggal"
          />
        </div>
        <div className="text-sm text-slate-600">
          <b className="text-teal-800">{rows.length}</b> pegawai WFA pada {formatTanggalID(tanggal)}
        </div>
      </div>

      {/* Table of current WFA on selected date */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0">
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Catatan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Dibuat</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <MapPinOff size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Tidak ada WFA pada tanggal ini</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Klik "Tambah WFA" untuk memilih pegawai.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`wfa-row-${r.user_id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{r.catatan || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.created_at ? formatTanggalID(r.created_at.slice(0, 10)) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                      <span className="text-xs text-slate-400 italic">—</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add dialog with multi-select */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="wfa-add-dialog">
          <DialogHeader>
            <DialogTitle>Tambah WFA untuk {formatTanggalID(tanggal)}</DialogTitle>
            <DialogDescription>
              Pilih pegawai yang akan WFA pada tanggal ini. Pegawai yang sudah WFA ditandai.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Cari nama / NPP / email…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="wfa-user-search"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllFiltered}
                className="gap-1.5 shrink-0"
                data-testid="wfa-select-all"
              >
                <Users size={13} /> Pilih Semua
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearSelection}
                className="shrink-0"
                data-testid="wfa-clear-selection"
              >
                Kosongkan
              </Button>
            </div>
            <div className="border border-slate-200 rounded-md flex-1 overflow-y-auto min-h-[240px] max-h-[40vh]">
              {filteredUsers.map((u) => {
                const isExisting = existingUserIds.has(u.id);
                const isChecked = !!selected[u.id];
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer ${
                      isExisting ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-teal-50/40"
                    }`}
                    data-testid={`wfa-user-${u.id}`}
                  >
                    <Checkbox
                      checked={isChecked || isExisting}
                      disabled={isExisting}
                      onCheckedChange={() => !isExisting && toggleSelect(u.id)}
                      data-testid={`wfa-check-${u.id}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">
                        {u.npp} • {u.email}
                      </div>
                    </div>
                    {isExisting ? (
                      <span className="text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                        Sudah WFA
                      </span>
                    ) : null}
                  </label>
                );
              })}
              {filteredUsers.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-500">Tidak ada pegawai cocok.</div>
              ) : null}
            </div>
            <div className="text-xs text-slate-600">
              Terpilih: <b className="text-teal-800" data-testid="wfa-selected-count">{Object.keys(selected).length}</b> pegawai
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Catatan (opsional)
              </Label>
              <Textarea
                className="mt-1.5"
                rows={2}
                placeholder="Contoh: WFH karena hujan lebat, dll."
                value={catatan}
                onChange={(e) => setCatatan(e.target.value)}
                data-testid="wfa-catatan"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdd(false)} data-testid="wfa-cancel-btn">
              Batal
            </Button>
            <Button
              onClick={saveBulk}
              disabled={saving || Object.keys(selected).length === 0}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="wfa-save-btn"
            >
              {saving ? "Menyimpan…" : `Simpan (${Object.keys(selected).length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
